import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { ApiError } from "@/services/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useFormResetWithQueryData } from "@/hooks/useFormResetWithQueryData";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useAgreementTypesDictionary } from "@/modules/agreement-types/hooks/useAgreementTypesDictionary";
import { AgreementType, AgreementTypeUpdate } from "@/types/agreementType";
import { agreementTypeUpdate } from "@/schemas/agreementType";
import AgreementTypeForm from "@/modules/agreement-types/components/AgreementTypeForm";
import AgreementTypeEditorFooter from "./AgreementTypeEditorFooter";

export default function AgreementTypeEditor({ id }: DictionaryContext) {
  const { t } = useTranslation();
  const config = useAgreementTypesDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { updateQueryDataForExistingItem, invalidateListQuery } =
    useQueryCacheUpdater(Module.AgreementTypes);
  const { can_update, can_delete } = useModulePermissions();

  const query = useQuery<AgreementType>({
    queryKey: [config.id, id],
    queryFn: async () => config.service.get(id),
  });

  const mutationFn: MutationFunction<
    AgreementType,
    AgreementTypeUpdate
  > = async (values) => config.service.patch(id, values);

  const deleteMutationFn: MutationFunction<void, void> = async () =>
    config.service.delete(id);

  const deleteMutation = useMutation({
    mutationFn: deleteMutationFn,
    onSuccess: () => {
      toast({
        title: t("agreementTypes.success.delete"),
      });
      invalidateListQuery();
      navigate(config.path);
    },
    onError: () => {
      toast({
        title: t("agreementTypes.errors.delete"),
        variant: "destructive",
      });
    },
  });

  const mutation = useMutation<AgreementType, ApiError, AgreementTypeUpdate>({
    mutationFn,
    onSuccess: (response) => {
      toast({
        title: labels.success.update,
      });
      updateQueryDataForExistingItem(response);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.update,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<AgreementTypeUpdate>({
    resolver: zodResolver(agreementTypeUpdate),
    defaultValues: {} as DefaultValues<AgreementTypeUpdate>,
    disabled: mutation.isPending || deleteMutation.isPending || !can_update,
  });

  useFormUnsavedChangesWarning(form);
  useFormResetWithQueryData(form, query);

  return (
    <DictionaryRecordContextProvider
      value={{
        query,
        config,
        mutation,
        form,
        id,
        deleteMutation: can_delete ? deleteMutation : undefined,
      }}
    >
      <DictionaryPageHeader />
      {query.isLoading && <DictionaryLoader />}
      {!query.isLoading && (
        <AgreementTypeForm footer={<AgreementTypeEditorFooter />} />
      )}
    </DictionaryRecordContextProvider>
  );
}
